import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { useGetAgentImpactApi } from "@/hooks/api"
import { FetchStatus } from "@/hooks/useFetch"
import { Agent } from "@/types"

export interface DeleteDialogProps {
  open: boolean
  agent: Agent
  onOpenChange?: (open: boolean) => void
  onConfirm?: (agent: Agent) => void
}

const DeleteDialog = ({
  open,
  agent,
  onOpenChange,
  onConfirm,
}: DeleteDialogProps) => {
  const { t } = useTranslation()
  const {
    data: impactResp,
    fetchFn: getAgentImpact,
    status: impactStatus,
  } = useGetAgentImpactApi(agent.id)

  // Fetch impact when dialog opens
  useEffect(() => {
    if (!open) return

    getAgentImpact()
  }, [open, agent.id, getAgentImpact])

  const isLoading = impactStatus === FetchStatus.Loading
  const impactedAgents = impactResp?.data?.impacted_agents || []

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("workspace.actionButton.deleteDialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("workspace.actionButton.deleteDialog.description", {
              name: ellipsis(agent.name, 54),
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Additional content outside of AlertDialogHeader */}
        <div className="space-y-3">
          {/* Show loading state */}
          {isLoading && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("workspace.actionButton.deleteDialog.checkingImpact")}
            </div>
          )}

          {/* Show impacted agents if any */}
          {!isLoading && impactedAgents.length > 0 && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-900 rounded-md">
              <div className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                {t("workspace.actionButton.deleteDialog.impactWarning")}
              </div>
              <ul className="space-y-2">
                {impactedAgents.map((impacted) => (
                  <li
                    key={impacted.agent_id}
                    className="text-sm text-yellow-700 dark:text-yellow-400"
                  >
                    <span className="font-medium">{impacted.agent_name}</span> -{" "}
                    {impacted.versions}
                  </li>
                ))}
              </ul>
              <div className="text-sm text-yellow-700 dark:text-yellow-400 mt-3">
                {t("workspace.actionButton.deleteDialog.impactConsequence")}
              </div>
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("workspace.actionButton.deleteDialog.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm?.(agent)}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600 dark:bg-white dark:hover:bg-gray-100 dark:text-black"
          >
            {t("workspace.actionButton.deleteDialog.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export { DeleteDialog }
export default DeleteDialog
